import React from 'react';
import { Link } from 'react-router-dom';

export default function AboutPage({ setShowLogin }: { setShowLogin?: (v: boolean) => void }) {
  return (
    <div className="about-page">
      <header className="about-hero">
        <img
          src="https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&q=80&w=1200"
          alt="Books and research"
          className="about-hero-img"
        />
        <p className="about-lead">
          JournalFeeds aggregates thousands of high-impact papers daily from thousands of peer-reviewed journals into a single, beautiful feed. Stay informed, vote on significant findings, and join the global conversation.
        </p>
      </header>

      <section className="about-section">
        <div className="about-split">
          <div className="about-split-text">
            <h2>Research moves fast. Now you can too.</h2>
            <p>
              Academic publishing is fragmented. With thousands of new papers every week across hundreds of disjointed sites, staying current is a full-time job.
            </p>
            <p>
              JournalFeeds solves this by pulling the latest RSS feeds from over 1,000 leading journals—Nature, Science, Lancet, NEJM, JAMA, BMJ, and more—into one centralized hierarchy. Browse by discipline, from Medicine and Law to Physics and Psychology.
            </p>
          </div>
          <img 
            src="https://images.unsplash.com/photo-1507413245164-6160d8298b31?auto=format&fit=crop&q=80&w=600" 
            alt="Scientist in lab" 
            className="about-split-img" 
          />
        </div>
      </section>

      <section className="about-section">
        <h2>Who is JournalFeeds for?</h2>
        <div className="about-grid">
          <div className="about-card">
            <h3>Researchers & Academics</h3>
            <p>Monitor your entire field without juggling dozens of alerts. Subscribe to specific sub-fields and see what's trending across adjacent disciplines.</p>
          </div>
          <div className="about-card">
            <h3>Clinicians & Practitioners</h3>
            <p>Maintain evidence-based excellence. From surgeons tracking new techniques to judges and senior counsel following landmark precedents, we bring the latest high-impact findings directly to your workflow.</p>
          </div>
          <div className="about-card">
            <h3>Patients & Advocates</h3>
            <p>Empower yourself with direct access to the latest clinical research on your condition. Track new studies in real-time as they are published by top medical journals.</p>
          </div>
          <div className="about-card">
            <h3>Industry R&D & Biotech</h3>
            <p>Stay ahead of the competitive landscape. Perfect for pharma, biotech, and legal-tech professionals who need to spot emerging technologies and breakthroughs before they hit the mainstream.</p>
          </div>
          <div className="about-card">
            <h3>Educators & Students</h3>
            <p>Discover fresh material for your thesis or lectures. Build public bookmark lists to share curated collections of key papers with students or colleagues.</p>
          </div>
          <div className="about-card">
            <h3>Journalists & Analysts</h3>
            <p>Spot emerging stories before they hit the press. Our <Link to="/hotp">"Hot Off the Press"</Link> section highlights papers about to make headlines, while "Research in the News" connects mainstream coverage back to original peer-reviewed sources.</p>
          </div>
        </div>
      </section>

      <section className="about-section">
        <div className="about-split reverse">
          <div className="about-split-text">
            <h2>Your personalized research hub.</h2>
            <p>
              JournalFeeds is free and open to everyone. While you can browse everything without an account, signing up unlocks a more powerful experience tailored to your interests.
            </p>
            <ul className="about-features">
              <li><strong>Personalized Feed:</strong> Choose your favorite journals and categories—or even save dynamic search results—to create a bespoke front page and research hub.</li>
              <li><strong>Community Voting:</strong> Help surface the most impactful research by upvoting significant findings.</li>
              <li><strong>Collaborative Discussion:</strong> Ask questions and share insights directly alongside any article.</li>
              <li><strong>Curated Lists:</strong> Organize your research into private or public bookmark lists.</li>
              <li><strong>Hot Off the Press:</strong> See which papers are about to make mainstream headlines — before the press picks them up.</li>
              <li><strong>Article Notes:</strong> Add public or private annotations to any article, organized automatically into your notes lists.</li>
            </ul>
          </div>
          <img 
            src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&q=80&w=600" 
            alt="Person writing and researching" 
            className="about-split-img" 
          />
        </div>
      </section>

      <section className="about-section" id="jfif">
        <h2>JFIF — The JournalFeeds Impact Factor</h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: '#444', maxWidth: 800 }}>
          Traditional impact factors measure how often academics cite each other. JFIF measures something different: how much the public is actually talking about a piece of research.
        </p>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: '#444', maxWidth: 800 }}>
          Every article on JournalFeeds receives a JFIF score computed from real signals across the web — mainstream media coverage, social media mentions on Bluesky, citation counts from OpenAlex and Semantic Scholar, and engagement within JournalFeeds itself (votes, comments, saves).
        </p>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: '#444', maxWidth: 800 }}>
          The result is a real-time measure of which research is breaking through from journals into public discourse. Browse the <Link to="/jf/trending">Trending</Link> page to see what the world is paying attention to right now.
        </p>

        <div className="about-grid" style={{ marginTop: 24 }}>
          <div className="about-card">
            <h3>News Coverage</h3>
            <p>Mentions in mainstream media outlets like The Guardian, Washington Post, BBC, NYT, and more.</p>
          </div>
          <div className="about-card">
            <h3>Social Media</h3>
            <p>Bluesky discussions and Twitter/X mentions tracked via Altmetric.</p>
          </div>
          <div className="about-card">
            <h3>Academic Citations</h3>
            <p>Citation counts from OpenAlex and Semantic Scholar, including influential citations.</p>
          </div>
          <div className="about-card">
            <h3>Community Engagement</h3>
            <p>Votes, comments, and saves by JournalFeeds users.</p>
          </div>
        </div>

        <p style={{ fontSize: 14, lineHeight: 1.7, color: '#666', marginTop: 20, maxWidth: 800 }}>
          <strong>Full transparency:</strong> Logged-in users can click any JFIF score to see the complete line-by-line calculation for that article — every signal, every weight, every point.
        </p>
        <p style={{ fontSize: 12, lineHeight: 1.6, color: '#999', marginTop: 12, maxWidth: 800, fontStyle: 'italic' }}>
          A small "Journal Influence" component is included based on publicly available citation metrics for each journal. It provides a minor baseline signal reflecting the journal's general reach, but is deliberately weighted low so that actual public discourse — news coverage, social mentions, community engagement — drives the vast majority of any article's JFIF score.
        </p>
      </section>

      <section className="about-section about-cta">
        <h2>Ready to stay ahead of the curve?</h2>
        <p>
          Join a community of thousands of professionals, students, and curious minds.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
          <Link to="/jf" className="create-post-btn" style={{ padding: '12px 32px', fontSize: '16px' }}>
            Browse the Feed
          </Link>
          {setShowLogin && (
            <button className="about-register-btn" style={{ marginTop: 0 }} onClick={() => setShowLogin(true)}>
              Create Free Account
            </button>
          )}
        </div>
      </section>

      <footer className="about-footer-note" style={{ textAlign: 'center' }}>
        <p>
          JournalFeeds is a project of the <a href="https://knowsyn.io" target="_blank" rel="noopener noreferrer">Knowledge Syndicate</a>. <br />
          Dedicated to making the world's high-impact research more accessible and actionable for everyone.
        </p>
      </footer >
    </div>
  );
}
